/**
 * useToday hook -- fetches the AI-generated daily plan from GET /today.
 *
 * Returns MUST-DO and SHOULD-DO items with reasoning, plus warnings.
 */

import { useState, useCallback, useEffect } from "react";
import { apiClient } from "../lib/api-client";
import type { Task } from "@atlas/shared";

/**
 * A single planned item with reasoning from the AI
 */
export interface PlannedItem {
  task: Task;
  reasoning: string;
  score: number;
}

/**
 * The daily plan structure returned by GET /today
 */
export interface TodayPlan {
  mustDo: PlannedItem[];
  shouldDo: PlannedItem[];
  warnings: string[];
}

/**
 * Return type of the useToday hook
 */
export interface UseTodayReturn {
  plan: TodayPlan | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Fetches the daily plan from GET /today endpoint.
 *
 * @param apiBaseUrl - API Gateway base URL (config.api.baseUrl)
 * @param date - Optional date in YYYY-MM-DD format (defaults to today on server)
 * @returns Daily plan state and refresh function
 */
export function useToday(apiBaseUrl: string, date?: string): UseTodayReturn {
  const [plan, setPlan] = useState<TodayPlan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const path = date ? `/today?date=${date}` : "/today";
      const data = await apiClient<TodayPlan>(apiBaseUrl, path);
      setPlan(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, date]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError(null);

      try {
        const path = date ? `/today?date=${date}` : "/today";
        const data = await apiClient<TodayPlan>(apiBaseUrl, path);
        if (!cancelled) {
          setPlan(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, date]);

  return { plan, isLoading, error, refresh };
}
